import { Dispatch, SetStateAction, useContext, useMemo, useState } from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import {
  Autocomplete,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useQueryClient } from "@tanstack/react-query";

import { UserDropdown } from "components/UserDropdown";
import { DatePicker } from "components/DatePicker";
import { Dropdown } from "components/Dropdown";
import { MultilineTextField } from "components/MultilineTextField";
import ProgressIndicatorOverlay from "components/ProgressIndicatorOverlay";
import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { UsersContext } from "contexts/UsersContext";
import { useFetchPostV2 } from "hooks/fetch/useFetchPostV2";
import { AxiosContext } from "contexts/AxiosContext";
import { FeaturesContext } from "contexts/FeaturesContext";
import ProgressIndicatorContainer from "components/ProgressIndicatorContainer";
import { CaseAction } from "interfaces/case-action";
import { AttachmentDetail } from "interfaces/attachment";
import { Dialog } from "components/Dialog";
import { CaseActionType } from "interfaces/case-action-type";
import { UNASSIGNED } from "core/constants";
import {
  IFileToUpload,
  NewAttachmentsList
} from "components/Attachments/NewAttachmentList";
import { ExistingAttachmentsList } from "components/Attachments/ExistingAttachmentsList";
import {
  CaseEventWithOptionLabel,
  useEventQuery,
  useEventsQuery
} from "hooks/queries/events";
import { PaginatedResponse } from "interfaces/utils";
import { getDraftJsText } from "core/utils/string";
import { dateFormat } from "core/datetime";
import { truncate } from "lodash";

const validationSchema = Yup.object({
  actionType: Yup.string()
    .label("Action")
    .required(),
  assignee: Yup.number()
    .label("Assignee")
    .nullable(true),
  requestedCompletionDate: Yup.date()
    .typeError("Requested completion date must be a valid date")
    .label("Requested completion date")
    .nullable(),
  description: Yup.string()
    .trim()
    .label("Description")
    .required()
});

interface CreateActionDialog {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  caseId: number;
  addNewAction: (response: CaseAction) => void;
  replaceExistingAction?: () => void;
  sourceEventId?: number;
}

interface CreateCaseActionForm {
  case: number;
  action_type: number;
  assignee: number | null;
  requested_completion_date: string | null;
  description: string | null;
  source_event: number | null;
  is_external?: boolean;
}

interface GetCaseActionTypesResponse {
  loading: boolean;
  error: boolean;
  response: { results: CaseActionType[]; count: number } | undefined;

  refetchCaseActionStatusTypes?(): Promise<void>;
}

const useGetCaseActionTypes = (): GetCaseActionTypesResponse => {
  const { loading, error, response } = useFetchGetV2(
    "case_action_types?ordering=name"
  );

  return {
    loading,
    error,
    response
  };
};

interface GetCaseAttachments {
  loading: boolean;
  error: boolean;
  response: AttachmentDetail[] | undefined;

  refetchCaseAttachments?(): Promise<void>;
}

const useGetCaseAttachments = (caseId: number): GetCaseAttachments => {
  const { loading, error, response } = useFetchGetV2(
    `cases/${caseId}/attachments`,
    caseId === undefined
  );

  return {
    loading,
    error,
    response
  };
};

interface PostAttachmentResponse {
  loading: boolean;
  postCallback: (body: any) => Promise<CaseAction>;
}

const usePostCaseAction = (): PostAttachmentResponse => {
  const { loading, postCallback } = useFetchPostV2("case_actions");

  return {
    loading,
    postCallback
  };
};

interface FormTypes {
  actionType: string;
  assignee: number | null;
  requestedCompletionDate: Date | string | null;
  description: string;
  attachments: number[];
  source_event: number | null;
  isExternal: boolean;
}

export const CreateCaseActionDialog = ({
  open,
  setOpen,
  caseId,
  addNewAction,
  sourceEventId
}: CreateActionDialog): JSX.Element => {
  const queryClient = useQueryClient();
  const { axios } = useContext(AxiosContext)!;
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);
  const { hasFeature } = useContext(FeaturesContext);
  const externalActionsEnabled = hasFeature("external_actions_for_work_orders");
  const {
    loading: usersLoading,
    error: usersError,
    nonServiceAccountUsers
  } = useContext(UsersContext);

  const [selectedCaseAttachmentIds, setSelectedCaseAttachmentIds] = useState<
    number[]
  >([]);

  const [filesToUpload, setFilesToUpload] = useState<IFileToUpload[]>([]);
  const [filesDropping, setFilesDropping] = useState<boolean>(false);

  const {
    loading: actionTypesLoading,
    error: actionTypesError,
    response: actionTypesResponse
  } = useGetCaseActionTypes();

  const {
    loading: caseAttachmentsLoading,
    error: caseAttachmentsError,
    response: caseAttachmentsResponse
  } = useGetCaseAttachments(caseId);

  const {
    postCallback: createCaseAction,
    loading: creatingCaseAction
  } = usePostCaseAction();

  const {
    data: multipleEventsData,
    isLoading: isLoadingEvents,
    isError: isErrorEvents
  } = useEventsQuery({
    case_id__in: caseId,
    type__in: "1",
    enabled: !sourceEventId
  });

  const {
    data: singleEventData,
    isLoading: isLoadingSingleEvent,
    isError: isErrorSingleEvent
  } = useEventQuery(sourceEventId ?? -1, !!sourceEventId);

  const eventsData:
    | PaginatedResponse<CaseEventWithOptionLabel[]>
    | undefined = useMemo(() => {
    if (sourceEventId && !isLoadingSingleEvent && !isErrorSingleEvent) {
      const dateTime = dateFormat(singleEventData?.date_time, {
        format: "Do MMM YYYY"
      } as any);

      const option_label = `${dateTime}: ${truncate(
        getDraftJsText(singleEventData?.description ?? ""),
        {
          length: 100,
          omission: "…"
        }
      )}`;

      return {
        count: 1,
        next: null,
        previous: null,
        results: [{ ...singleEventData, option_label }]
      };
    } else if (!isLoadingEvents && !isErrorEvents) {
      return multipleEventsData;
    }
  }, [
    sourceEventId,
    isLoadingSingleEvent,
    isErrorSingleEvent,
    isLoadingEvents,
    isErrorEvents,
    singleEventData,
    multipleEventsData
  ]);

  const handleCompleted = async (response: CaseAction): Promise<void> => {
    addSnackbarToQueue({
      message: `Case action created.`,
      severity: "success"
    });
    closeDialog();
    await queryClient.invalidateQueries(["streamFeed", caseId]);
    addNewAction(response);
  };

  const handleError = (): void => {
    addSnackbarToQueue({
      message: "Case action failed to save.",
      severity: "error"
    });
    setOpen(false);
  };

  const convertDate = (date: string): string => {
    let formattedDate = new Date(date);
    const offset = formattedDate?.getTimezoneOffset();

    formattedDate = new Date(formattedDate.getTime() - offset * 60 * 1000);

    // Remove time from date
    return formattedDate.toISOString().split("T")[0];
  };

  const initialFormValues: FormTypes = {
    actionType: "",
    assignee: null,
    requestedCompletionDate: null,
    description: "",
    attachments: [],
    source_event: sourceEventId ?? null,
    isExternal: externalActionsEnabled
  };

  const formik = useFormik<FormTypes>({
    initialValues: initialFormValues,
    enableReinitialize: true,
    validationSchema,
    onSubmit: async values => {
      let dueDate = null;

      if (values?.requestedCompletionDate) {
        dueDate = convertDate(values.requestedCompletionDate as string);
      }

      const createCaseActionFormBody: CreateCaseActionForm = {
        case: caseId,
        action_type: Number(values.actionType),
        assignee: values?.assignee ?? null,
        requested_completion_date: dueDate ?? null,
        description: values.description?.trim(),
        source_event: values.source_event,
        is_external: values.isExternal
      };

      try {
        const caseAction = await createCaseAction(createCaseActionFormBody);

        // Attach selected case attachments
        const caseAttachmentPostRequests = selectedCaseAttachmentIds.map(
          attachmentId => {
            return axios.post(
              `case_actions/${caseAction.id}/attachments/attach/${attachmentId}`
            );
          }
        );

        await Promise.all(caseAttachmentPostRequests);

        // Upload files added
        for (const file of filesToUpload) {
          const formData = new FormData();
          formData.append("file", file.contents);

          await axios
            .post(`case_actions/${caseAction.id}/attachments`, formData)
            .catch(error => {
              console.error("Error uploading file:", error);
            });
        }

        await handleCompleted(caseAction);
      } catch {
        handleError();
      }
    }
  });

  const sourceEventValue = useMemo(
    () =>
      eventsData?.results.find(
        option => option.id === sourceEventId ?? formik.values.source_event
      ) ?? null,
    [eventsData?.results, formik.values.source_event, sourceEventId]
  );

  const setDueDate = (date: string): void => {
    formik.setFieldValue("requestedCompletionDate", date);
  };

  const closeDialog = (): void => {
    setSelectedCaseAttachmentIds([]);
    setFilesToUpload([]);
    formik.resetForm();
    setOpen(false);
  };

  const isLoading =
    actionTypesLoading ||
    usersLoading ||
    creatingCaseAction ||
    caseAttachmentsLoading ||
    formik.isSubmitting;

  return (
    <Dialog
      dialogId="dialog-create-case-action"
      open={open}
      title={"Create Case Action"}
      confirmText="submit"
      size="medium"
      actionsDisabled={isLoading || filesDropping}
      onSubmit={formik.handleSubmit}
      onClose={closeDialog}
      aria-label="new-case-action-form"
      data-qa="CP-Form-New-Case-Action"
      allowClickaway={false}
    >
      <ProgressIndicatorOverlay loading={isLoading} />
      <StyledDivDialogContent>
        <Stack gap={2}>
          {externalActionsEnabled && (
            <FormControlLabel
              control={
                <Checkbox
                  name="isExternal"
                  checked={formik.values.isExternal}
                  onChange={formik.handleChange}
                  data-qa="CP-Checkbox-Send-To-Work-Order-System"
                />
              }
              label="Send to work order system"
            />
          )}
          <Dropdown
            fullWidth
            id="actionType"
            name="actionType"
            label="Action"
            value={actionTypesLoading ? "" : formik.values.actionType}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            disabled={
              actionTypesLoading ||
              !actionTypesResponse?.count ||
              actionTypesError
            }
            data-qa="CP-Select-Action-Type"
            error={
              formik.touched.actionType && Boolean(formik.errors.actionType)
            }
            helperText={
              formik.touched.actionType && formik.errors.actionType
                ? formik.errors.actionType
                : ""
            }
            required
          >
            {actionTypesResponse?.results?.map(
              ({ name, id }: CaseActionType) => (
                <MenuItem key={id} value={id}>
                  {name}
                </MenuItem>
              )
            ) ?? []}
          </Dropdown>
          <MultilineTextField
            id="nca-form-problem-description"
            name="description"
            label="Description"
            value={formik.values.description}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={
              formik.touched.description && Boolean(formik.errors.description)
            }
            helperText={formik.touched.description && formik.errors.description}
            data-qa="CP-Text-Action-Description"
            required
            fullWidth
          />
          <Autocomplete
            data-qa="CP-Select-Source-Event"
            disabled={!!sourceEventId}
            loading={isLoadingEvents}
            loadingText="Loading reports..."
            options={eventsData?.results ?? []}
            value={sourceEventValue}
            getOptionLabel={option => option.option_label}
            renderInput={params => (
              <TextField
                {...params}
                label="Linked Diagnostic Report"
                name="source_event"
                error={
                  formik.touched.source_event &&
                  Boolean(formik.errors.source_event)
                }
                helperText={
                  formik.touched.source_event && formik.errors.source_event
                }
              />
            )}
            onChange={(_event, value) => {
              formik.setFieldValue("source_event", value?.id);
            }}
            onBlur={formik.handleBlur}
          />
          <DatePicker
            id="requestedCompletionDate"
            date={formik.values.requestedCompletionDate as Date}
            onChange={setDueDate}
            label="Due Date"
            clearable
            disableFuture={false}
            fullWidth
            size="medium"
            slotProps={{
              textField: {
                id: "requestedCompletionDate",
                onBlur: formik.handleBlur,
                error:
                  formik.touched.requestedCompletionDate &&
                  Boolean(formik.errors.requestedCompletionDate),
                helperText:
                  formik.touched.requestedCompletionDate &&
                  formik.errors.requestedCompletionDate
              }
            }}
          />
          <UserDropdown
            label="Assignee"
            users={nonServiceAccountUsers}
            unassignedValue={UNASSIGNED}
            error={usersError}
            helperText={usersError ? "Error getting users." : undefined}
            disabled={usersLoading}
            selectedUserId={formik.values.assignee}
            onChangeSelectedUserId={newValue =>
              formik.setFieldValue("assignee", newValue)
            }
            avatarTooltip={
              "Assignee is the person the action is being assigned to"
            }
          />

          <Stack>
            <NewAttachmentsList
              attachmentsToUpload={filesToUpload}
              setAttachmentsToUpload={setFilesToUpload}
              filesDropping={filesDropping}
              setFilesDropping={setFilesDropping}
            />
            {((caseAttachmentsResponse &&
              caseAttachmentsResponse?.length > 0) ||
              caseAttachmentsLoading) && (
              <Typography component="h3" fontWeight="bold">
                Case attachments to include
              </Typography>
            )}
            <ProgressIndicatorContainer
              loading={caseAttachmentsLoading}
              error={caseAttachmentsError}
            >
              {caseAttachmentsResponse && (
                <ExistingAttachmentsList
                  initialAttachments={caseAttachmentsResponse}
                  setSelectedAttachmentIds={setSelectedCaseAttachmentIds}
                />
              )}
            </ProgressIndicatorContainer>
          </Stack>
        </Stack>
      </StyledDivDialogContent>
    </Dialog>
  );
};

const StyledDivDialogContent = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
StyledDivDialogContent.displayName = "StyledDivDialogContent";
